import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  FileSignature, Download, Loader2, Building2, User, AlertCircle,
  Plus, Trash2, FileText, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const emptyForm = {
  azienda_id: '',
  pdv_id: '',
  ragione_sociale: '',
  indirizzo: '',
  cap: '',
  comune: '',
  provincia: '',
  consulente_nome: '',
};

export default function DichiarazioneHaccpPage() {
  const { user } = useAuth();
  const role = user?.role || '';
  const userAziendaId = user?.azienda_id || '';
  const isAdmin = ['super_admin', 'admin', 'amministratore'].includes(role);
  const isConsulente = ['consulente_haccp', 'haccp_admin', 'haccp_responsabile'].includes(role);
  const isAziendaUser = ['azienda', 'gerente'].includes(role);
  const canCreate = isAdmin || isConsulente || isAziendaUser || ['studio_tecnico'].includes(role);

  const [aziende, setAziende] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [dichiarazioni, setDichiarazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [dichRes, azRes, pdvRes] = await Promise.all([
        api.get('/haccp/dichiarazioni'),
        api.get('/aziende').catch(() => ({ data: [] })),
        api.get('/pdv').catch(() => ({ data: [] })),
      ]);
      setDichiarazioni(dichRes.data || []);
      const azItems = Array.isArray(azRes.data) ? azRes.data : (azRes.data?.items || []);
      const pdvItems = Array.isArray(pdvRes.data) ? pdvRes.data : (pdvRes.data?.items || []);
      // Se l'utente è "azienda/gerente" filtra al solo proprio
      if (isAziendaUser && userAziendaId) {
        setAziende(azItems.filter(a => (a.id || a._id) === userAziendaId));
        setPdvList(pdvItems.filter(p => p.azienda_id === userAziendaId));
      } else {
        setAziende(azItems);
        setPdvList(pdvItems);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Apre dialog (pre-popola consulente per consulenti HACCP, e azienda per ruoli "azienda")
  const openNewDialog = () => {
    const newForm = { ...emptyForm };
    if (isConsulente) newForm.consulente_nome = user?.full_name || '';
    // Forza azienda dell'utente se è azienda/gerente
    if (isAziendaUser && userAziendaId) {
      const az = aziende.find(a => (a.id || a._id) === userAziendaId);
      if (az) {
        newForm.azienda_id = userAziendaId;
        newForm.ragione_sociale = az.denominazione || az.ragione_sociale || '';
        newForm.indirizzo = az.sede_legale || '';
        newForm.cap = az.cap || '';
        newForm.comune = az.citta || az.comune || '';
        newForm.provincia = az.provincia || '';
      }
    }
    setForm(newForm);
    setOpenDialog(true);
  };

  const onSelectAzienda = (azId) => {
    const az = aziende.find(a => (a.id || a._id) === azId);
    if (!az) return;
    setForm(f => ({
      ...f,
      azienda_id: azId,
      pdv_id: '',
      ragione_sociale: az.denominazione || az.ragione_sociale || '',
      indirizzo: az.sede_legale || '',
      cap: az.cap || '',
      comune: az.citta || az.comune || '',
      provincia: az.provincia || '',
    }));
  };
  const onSelectPdv = (pdvId) => {
    const p = pdvList.find(x => (x.id || x._id) === pdvId);
    if (!p) return;
    setForm(f => ({
      ...f,
      pdv_id: pdvId,
      azienda_id: p.azienda_id || f.azienda_id,
      ragione_sociale: p.company_name || p.name || f.ragione_sociale,
      indirizzo: p.address || '',
      cap: p.cap || '',
      comune: p.city || '',
      provincia: p.province || p.region || '',
    }));
  };

  const submitDichiarazione = async () => {
    if (!form.ragione_sociale.trim()) {
      toast.error('Ragione sociale obbligatoria');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post('/haccp/dichiarazioni', form);
      toast.success('✅ Dichiarazione creata');
      // Scarica subito il .docx
      const docxR = await api.get(`/haccp/dichiarazioni/${r.data.id}/docx`, { responseType: 'blob' });
      const url = URL.createObjectURL(docxR.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Dichiarazione_HACCP_${form.ragione_sociale.replace(/\s+/g, '_')}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      setOpenDialog(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione');
    } finally {
      setSaving(false);
    }
  };

  const downloadDocx = async (d) => {
    try {
      const r = await api.get(`/haccp/dichiarazioni/${d.id}/docx`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Dichiarazione_HACCP_${d.ragione_sociale.replace(/\s+/g, '_')}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Errore download'); }
  };

  const deleteDichiarazione = async (d) => {
    if (!window.confirm(`Eliminare la dichiarazione di "${d.ragione_sociale}"?`)) return;
    try {
      await api.delete(`/haccp/dichiarazioni/${d.id}`);
      toast.success('Dichiarazione eliminata');
      setDichiarazioni(arr => arr.filter(x => x.id !== d.id));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore eliminazione'); }
  };

  return (
    <div className="space-y-6" data-testid="dichiarazione-haccp-page">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileSignature className="w-7 h-7 text-amber-600" /> Dichiarazione Autocontrollo HACCP
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Genera, scarica ed elimina le dichiarazioni di autocontrollo HACCP per ogni azienda.
            {isAziendaUser && ' Puoi gestire solo le dichiarazioni della tua azienda.'}
          </p>
        </div>
        {canCreate && (
          <Button onClick={openNewDialog} className="bg-amber-600 hover:bg-amber-700" data-testid="new-dichiarazione-btn">
            <Plus className="w-4 h-4 mr-1" /> Crea
          </Button>
        )}
      </motion.div>

      {loading ? (
        <p className="text-slate-500">Caricamento...</p>
      ) : dichiarazioni.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="pt-10 pb-10 text-center">
            <FileSignature className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600">Nessuna dichiarazione ancora creata. Usa il pulsante <strong>Crea</strong> in alto a destra.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {dichiarazioni.map(d => (
            <Card key={d.id} className="hover:shadow-md transition-shadow" data-testid={`dich-${d.id}`}>
              <CardContent className="pt-4">
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <FileText className="w-4 h-4 text-amber-600" />
                      <strong className="text-lg">{d.ragione_sociale}</strong>
                      {d.consulente_nome && (
                        <Badge variant="outline" className="text-[10px]">Consulente: {d.consulente_nome}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {[d.indirizzo, d.cap, d.comune, d.provincia ? `(${d.provincia})` : ''].filter(Boolean).join(' ')}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Creata da {d.created_by_name} il {new Date(d.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => downloadDocx(d)}
                      data-testid={`download-${d.id}`}>
                      <Download className="w-3 h-3 mr-1" /> .docx
                    </Button>
                    {d.can_delete && (
                      <Button size="sm" variant="ghost" onClick={() => deleteDichiarazione(d)}
                        className="text-red-600 hover:bg-red-50" data-testid={`delete-${d.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* DIALOG: Nuova Dichiarazione */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova Dichiarazione HACCP</DialogTitle>
            <DialogDescription>
              Seleziona l&apos;azienda (e opzionalmente una sua UL) oppure compila i dati manualmente.
              Verrà generato e scaricato il .docx, e la dichiarazione resterà visibile in elenco.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Step 1: Azienda */}
            {!isAziendaUser && (
              <div>
                <Label>Azienda *</Label>
                <select value={form.azienda_id} onChange={e => onSelectAzienda(e.target.value)}
                  className="w-full border rounded p-2 text-sm" data-testid="az-select">
                  <option value="">— Scegli azienda —</option>
                  {aziende.map(a => {
                    const id = a.id || a._id;
                    return <option key={id} value={id}>{a.denominazione || a.ragione_sociale}</option>;
                  })}
                </select>
              </div>
            )}
            {/* Step 1b: UL opzionale */}
            {form.azienda_id && pdvList.filter(p => p.azienda_id === form.azienda_id).length > 0 && (
              <div>
                <Label>Unità Locale (opzionale)</Label>
                <select value={form.pdv_id} onChange={e => onSelectPdv(e.target.value)}
                  className="w-full border rounded p-2 text-sm" data-testid="pdv-select">
                  <option value="">— Solo sede legale —</option>
                  {pdvList.filter(p => p.azienda_id === form.azienda_id).map(p => {
                    const id = p.id || p._id;
                    return <option key={id} value={id}>{p.name} {p.city ? `— ${p.city}` : ''}</option>;
                  })}
                </select>
              </div>
            )}
            {/* Step 2: Dati editabili */}
            <div className="grid grid-cols-1 gap-2 pt-2 border-t">
              <div>
                <Label>Ragione sociale *</Label>
                <Input value={form.ragione_sociale}
                  onChange={e => setForm({ ...form, ragione_sociale: e.target.value })}
                  data-testid="rs-input" />
              </div>
              <div>
                <Label>Indirizzo</Label>
                <Input value={form.indirizzo}
                  onChange={e => setForm({ ...form, indirizzo: e.target.value })}
                  data-testid="indirizzo-input" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label>CAP</Label>
                  <Input value={form.cap} onChange={e => setForm({ ...form, cap: e.target.value })}
                    data-testid="cap-input" />
                </div>
                <div className="col-span-2">
                  <Label>Comune</Label>
                  <Input value={form.comune} onChange={e => setForm({ ...form, comune: e.target.value })}
                    data-testid="comune-input" />
                </div>
              </div>
              <div>
                <Label>Provincia</Label>
                <Input value={form.provincia} onChange={e => setForm({ ...form, provincia: e.target.value })}
                  data-testid="provincia-input" className="max-w-xs" />
              </div>
              <div>
                <Label>Consulente HACCP (opzionale)</Label>
                <Input value={form.consulente_nome}
                  onChange={e => setForm({ ...form, consulente_nome: e.target.value })}
                  placeholder="Es. Ing. Mario Rossi"
                  data-testid="consulente-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Annulla</Button>
            <Button onClick={submitDichiarazione} disabled={saving || !form.ragione_sociale.trim()}
              className="bg-amber-600 hover:bg-amber-700" data-testid="submit-dichiarazione">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSignature className="w-4 h-4 mr-1" />}
              Crea e scarica .docx
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
